import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_DATA } from './SEOData';
import { 
  getOrganizationSchema, 
  getSoftwareApplicationSchema, 
  getWebPageSchema
} from './StructuredData';

const SEOWrapper = ({ 
  page,
  title, 
  description, 
  keywords, 
  customTitle,
  customDescription,
  customKeywords,
  canonicalUrl, 
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  structuredData,
  noIndex = false,
  noFollow = false,
  children
}) => {
  // Get SEO data from the page prop or use custom values
  const seoData = page && SEO_DATA[page] ? SEO_DATA[page] : {};
  const finalTitle = customTitle || title || seoData.title || 'Ekalsutra - School Management ERP Software';
  const finalDescription = customDescription || description || seoData.description || 'Leading school management ERP software with comprehensive features for educational institutions.';
  const finalKeywords = customKeywords || keywords || seoData.keywords || 'school management software, education ERP, school administration';
  const finalOgImage = ogImage || seoData.ogImage || '/landingPage/homeImg.svg';
 
  const baseUrl = 'https://ekalsutra.com';

  // Clean canonical URL - strip query parameters
  const fullCanonicalUrl = canonicalUrl ? 
    `${baseUrl}${canonicalUrl.split('?')[0]}` : 
    baseUrl + (page === 'home' ? '' : `/${page}`);
  
  const fullOgImage = finalOgImage ? `${baseUrl}${finalOgImage}` : `${baseUrl}/landingPage/Ekalsutra_Logo.svg`;
  
  const robotsContent = `${noIndex ? 'noindex' : 'index'},${noFollow ? 'nofollow' : 'follow'}`;

  // Generate structured data based on page type
  const getStructuredDataForPage = (pageType, metadata) => {
    const baseSchemas = [getOrganizationSchema()];
    
    // Add page-specific schemas
    switch (pageType) {
      case 'home':
        return [
          ...baseSchemas, 
          getSoftwareApplicationSchema(),
          getWebPageSchema(metadata.title, metadata.description)
        ];
      case 'about':
      case 'contact':
      case 'terms':
      case 'privacy':
      case 'refund':
        return [
          ...baseSchemas,
          getWebPageSchema(metadata.title, metadata.description)
        ];
      default:
        return [...baseSchemas, getSoftwareApplicationSchema()];
    }
  };

  // Generate structured data if none provided
  const finalStructuredData = structuredData || (page ? getStructuredDataForPage(page, {
    title: finalTitle,
    description: finalDescription,
    url: fullCanonicalUrl
  }) : getOrganizationSchema());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{finalTitle}</title>
        <meta name="description" content={finalDescription} />
        {finalKeywords && <meta name="keywords" content={finalKeywords} />}
        <meta name="robots" content={robotsContent} />
        <link rel="canonical" href={fullCanonicalUrl} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:type" content={ogType} />
        <meta property="og:url" content={fullCanonicalUrl} />
        <meta property="og:title" content={finalTitle} />
        <meta property="og:description" content={finalDescription} />
        <meta property="og:image" content={fullOgImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Ekalsutra - School Management ERP" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:url" content={fullCanonicalUrl} />
        <meta name="twitter:title" content={twitterTitle || finalTitle} />
        <meta name="twitter:description" content={twitterDescription || finalDescription} />
        <meta name="twitter:image" content={fullOgImage} />
        <meta name="twitter:site" content="@EkalSutra" />
        <meta name="twitter:creator" content="@EkalSutra" />
        
        {/* Structured Data */}
        {finalStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(finalStructuredData)}
          </script>
        )}
      </Helmet>
      {children}
    </>
  );
};

export default SEOWrapper;
